import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { intlShape } from "@ai360/core/intl";
import { Bucket, BucketHeader, Checkbox, SearchInput } from "~/core";
import { messages } from "../i18n-messages";
import { IReportType, ISelectReportType } from "../data/interfaces";
import { useIntl } from "react-intl";

export interface IReportTypesProps {
    reportTypes?: IReportType[];
    selectReportType?: (report: ISelectReportType & { checked?: boolean }) => void;
    selectedReportTypes?: string[];
    withRecentUsed?: boolean;
    onSearchChange?: (value?: string) => void;
}

const ReportTypes = ({
    reportTypes,
    selectReportType,
    selectedReportTypes,
    withRecentUsed,
}: IReportTypesProps): JSX.Element => {
    const { formatMessage } = useIntl();
    const [searchValue, setSearchValue] = useState<string>(null);

    const isReportTypeSelected = ({ reportTypeGuid }) => {
        return selectedReportTypes.indexOf(reportTypeGuid) > -1;
    };

    const getLabelString = () => {
        return `1. ${formatMessage(messages.selectReports)}:`;
    };

    const recentTypes = useMemo(
        () =>
            withRecentUsed
                ? reportTypes
                      ?.filter((x) => Boolean(x.lastRun))
                      .sort(
                          (a, b) =>
                              b.lastRun.localeCompare(a.lastRun) ||
                              a.reportTypeDisplayName.localeCompare(b.reportTypeDisplayName)
                      )
                      .slice(0, 10)
                      .sort((a, b) =>
                          a.reportTypeDisplayName.localeCompare(b.reportTypeDisplayName)
                      ) ?? []
                : [],
        [reportTypes, withRecentUsed]
    );

    const filteredReportTypes = useMemo(
        () =>
            !searchValue
                ? reportTypes
                : reportTypes.filter((x) =>
                      x.reportTypeDisplayName.toLocaleLowerCase().includes(searchValue)
                  ),
        [searchValue, reportTypes]
    );

    const outputCheckbox = (reportType: IReportType, index: number) => (
        <Checkbox
            key={index}
            value={isReportTypeSelected(reportType)}
            label={reportType.reportTypeDisplayName}
            onChange={(event, value) =>
                selectReportType({
                    name: reportType.reportTypeName,
                    guid: reportType.reportTypeGuid,
                    checked: value,
                })
            }
            testId={`report-types-report-type-checkbox-${index}`}
        />
    );

    const outputRecentLayout = () =>
        reportTypes.length > 0 && (
            <div className="type-buckets">
                {recentTypes.length > 0 && !searchValue && (
                    <Bucket
                        isExpanded
                        showSymbol={true}
                        isCollapsible={true}
                        testId="report-types-recent-bucket"
                    >
                        <BucketHeader>{formatMessage(messages.recent)}</BucketHeader>
                        {recentTypes.map((reportType, index) => outputCheckbox(reportType, index))}
                    </Bucket>
                )}
                <Bucket
                    isExpanded
                    showSymbol={true}
                    isCollapsible={true}
                    testId="report-types-all-bucket"
                >
                    <BucketHeader>{formatMessage(messages.all)}</BucketHeader>
                    {filteredReportTypes?.map((reportType, index) =>
                        outputCheckbox(reportType, index + recentTypes.length)
                    )}
                </Bucket>
            </div>
        );

    return !withRecentUsed ? (
        <div className="report-types">
            <div className="options-header">{getLabelString()}</div>
            {withRecentUsed
                ? outputRecentLayout()
                : reportTypes?.map((reportType, index) => outputCheckbox(reportType, index))}
        </div>
    ) : (
        <div className="report-types">
            <div className="with-recent">
                <div className="options-header">
                    <div>{getLabelString()}</div>
                    <SearchInput
                        onSearchChange={(value) => setSearchValue(value?.toLocaleLowerCase())}
                    />
                </div>
                {outputRecentLayout()}
            </div>
        </div>
    );
};

ReportTypes.propTypes = {
    intl: intlShape,
    reportTypes: PropTypes.array,
    selectReportType: PropTypes.func,
    selectedReportTypes: PropTypes.array,
    withRecentUsed: PropTypes.bool,
    onSearchChange: PropTypes.func,
};

export default ReportTypes;
